import { Box, Typography } from '@mui/material';
import useDeviceGroup from 'hooks/useDeviceGroup';
import { useTranslation } from 'next-i18next';
import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import type { FC } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';
import Modal from 'components/Modal';
import FilledTextField from 'components/MuiCustom/FilledTextField';
import StatusMessageBox from 'components/StatusMessageBox';

import { useAppDispatch } from 'redux-store';
import type { DeviceGroupId } from 'redux-store/slices/deviceGroups/types';
import getAllDeviceGroupsThunk from 'redux-store/thunks/deviceGroups/getAllDeviceGroupsThunk';
import renameDeviceGroupThunk from 'redux-store/thunks/deviceGroups/renameDeviceGroupThunk';

export interface RenameDeviceGroupModalProps extends DefaultPopupProps {
  groupId: DeviceGroupId | null;
}

const RenameDeviceGroupModal: FC<RenameDeviceGroupModalProps> = ({
  groupId,
  onClose,
  open,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const groupNameInputRef = useRef<HTMLInputElement>(null);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [inputError, setInputError] = useState<string | null>(null);

  const deviceGroup = useDeviceGroup(groupId ?? undefined);

  const currentName = useMemo(() => deviceGroup?.name ?? '', [deviceGroup]);

  const [name, setName] = useState<string>(currentName);

  useEffect(() => {
    if (currentName) {
      setName(currentName);
    }
  }, [currentName]);

  const validateName = useCallback(
    (inputName: string): string => {
      if (!inputName) {
        return t(
          'common:pages.device_groups.add_device_group.errors.name_required',
        );
      }

      if (inputName.length > 50) {
        return t(
          'common:pages.device_groups.add_device_group.group_name_max_length',
        );
      }

      return '';
    },
    [t],
  );

  const handleChangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value;
      const validationError = validateName(newName);
      setInputError(validationError);
      setName(newName);
    },
    [validateName],
  );

  const handleSave = useCallback(async () => {
    if (isLoading) return;
    if (groupId === null) return;

    const validationError = validateName(name);

    if (validationError) {
      setInputError(validationError);

      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await dispatch(
        renameDeviceGroupThunk({ deviceGroupId: groupId, data: { name } }),
      ).unwrap();

      if (result.success) {
        await dispatch(getAllDeviceGroupsThunk({})).unwrap();
        setSuccess(true);

        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 1250);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err as string);
    } finally {
      setLoading(false);
    }
  }, [dispatch, groupId, isLoading, name, onClose, validateName]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        groupNameInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  return (
    <Modal open={open} onClose={onClose}>
      {!success ? (
        <Box
          height="100%"
          width="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{ overflowY: 'auto' }}
        >
          <Box mb={4} width="100%">
            <Typography
              variant="h2"
              textAlign="center"
              mb={2}
              data-testid="rename-device-group-modal"
            >
              {t('common:pages.device_groups.rename_group.title')}
            </Typography>
            <Typography variant="body1" mb={2} textAlign="center">
              {t('common:pages.device_groups.rename_group.description')}
            </Typography>
          </Box>
          <Box mb={4} width="100%">
            <form
              onSubmit={e => {
                e.preventDefault();
                handleSave();
              }}
            >
              <FilledTextField
                fullWidth
                type="text"
                label={t('common:pages.device_groups.rename_group.group_name')}
                value={name}
                onChange={handleChangeName}
                inputProps={{ 'data-testid': 'new-name-input' }}
                error={Boolean(inputError)}
                helperText={inputError}
                inputRef={groupNameInputRef}
              />
            </form>
          </Box>
          <ErrorText error={error} mb={2} />
          <Box width="100%" display="flex" flexDirection="row" gap={2} pt={2}>
            <Button
              variant="flat"
              color="neutral"
              fullWidth
              onClick={onClose}
              size="large"
              data-testid="cancel-button"
            >
              {t('common:cancel')}
            </Button>
            <Button
              variant="flat"
              fullWidth
              size="large"
              onClick={handleSave}
              disabled={isLoading || Boolean(inputError)}
              data-testid="submit"
            >
              {t('common:save')}
            </Button>
          </Box>
        </Box>
      ) : (
        <StatusMessageBox
          type="success"
          message={t('common:pages.device_groups.rename_group.group_renamed')}
        />
      )}
    </Modal>
  );
};

export default RenameDeviceGroupModal;
